import { LoaderSizes } from "atoms/constants/loader-sizes";
import Loader from "atoms/loaders/loader";
import ProgressBar, { ProgressBarStyles } from "atoms/progress/progress-bar";
import Paragraph from "atoms/typography/paragraph";
import AlertLevels from "constants/alert-levels";
import JobStatuses from "models/enumerations/jobs/job-statuses";
import GroupMigrationRequestRecord from "models/view-models/group-migration-request-record";
import AlertBanner from "molecules/alerts/alert-banner";
import BaseModalFooter from "molecules/modals/footers/base-modal-footer";
import React, { useMemo } from "react";
import { usePollJob } from "utilities/hooks/domain/jobs/use-poll-job";
import StringUtils from "utilities/string-utils";

const CSS_CLASS_NAME = "c-support-user-team-manage-modal";

// using pascal case here to avoid serializing debugJson
interface MigrateJobDebugJson {
    Status?: string;
    AddMembersJobId?: number;
    SyncJobId?: number;
    DataMigrationJobId?: number;
    Errors?: string;
}

interface JobProgressDebugJson {
    progressCount?: number;
    progressTotal?: number;
    errorCount?: number;
}

interface ModalPhaseMigrationProgressProps {
    migrationRequest: GroupMigrationRequestRecord;
    onClose: () => void;
}

const ModalPhaseMigrationProgress = ({
    migrationRequest,
    onClose,
}: ModalPhaseMigrationProgressProps) => {
    const { job: mainJob, isError: mainJobError } = usePollJob(
        migrationRequest.jobId ?? null
    );

    const mainDebugJson = useMemo(
        () => parseDebugJson<MigrateJobDebugJson>(mainJob?.debugJson),
        [mainJob?.debugJson]
    );

    const addMembersJobId = mainDebugJson?.AddMembersJobId ?? null;
    const syncJobId = mainDebugJson?.SyncJobId ?? null;
    const dataMigrationJobId = mainDebugJson?.DataMigrationJobId ?? null;

    const { job: addMembersJob, isError: addMembersJobError } =
        usePollJob(addMembersJobId);
    const { job: syncJob, isError: syncJobError } = usePollJob(syncJobId);
    const { job: dataMigrationJob, isError: dataMigrationJobError } =
        usePollJob(dataMigrationJobId);

    const addMembersProgress = useMemo(
        () => parseDebugJson<JobProgressDebugJson>(addMembersJob?.debugJson),
        [addMembersJob?.debugJson]
    );

    const syncProgress = useMemo(
        () => parseDebugJson<JobProgressDebugJson>(syncJob?.debugJson),
        [syncJob?.debugJson]
    );

    const dataMigrationProgress = useMemo(
        () => parseDebugJson<JobProgressDebugJson>(dataMigrationJob?.debugJson),
        [dataMigrationJob?.debugJson]
    );

    const mainJobFinished =
        mainJob?.status === JobStatuses.Completed ||
        mainJob?.status === JobStatuses.Errored;

    const addMembersFinished =
        addMembersJobId == null ||
        addMembersJob?.status === JobStatuses.Completed ||
        addMembersJob?.status === JobStatuses.Errored;

    const syncFinished =
        syncJobId == null ||
        syncJob?.status === JobStatuses.Completed ||
        syncJob?.status === JobStatuses.Errored;

    const dataMigrationFinished =
        dataMigrationJobId == null ||
        dataMigrationJob?.status === JobStatuses.Completed ||
        dataMigrationJob?.status === JobStatuses.Errored;

    const allFinished =
        mainJobFinished &&
        addMembersFinished &&
        syncFinished &&
        dataMigrationFinished;

    const hasError =
        mainJobError ||
        addMembersJobError ||
        syncJobError ||
        dataMigrationJobError ||
        mainJob?.status === JobStatuses.Errored ||
        addMembersJob?.status === JobStatuses.Errored ||
        syncJob?.status === JobStatuses.Errored ||
        dataMigrationJob?.status === JobStatuses.Errored;

    const isWaitingForNestedJobs =
        mainJobFinished &&
        addMembersJobId == null &&
        syncJobId == null &&
        dataMigrationJobId == null;

    const renderJobProgress = (
        label: string,
        progress: JobProgressDebugJson | null,
        isFinished: boolean,
        hasJobError: boolean,
        isLoading: boolean
    ) => {
        const hasProgressData =
            progress?.progressTotal != null && progress.progressTotal > 0;

        const percent = hasProgressData
            ? Math.round(
                  ((progress!.progressCount ?? 0) / progress!.progressTotal!) *
                      100
              )
            : 0;

        const showPlaceholder =
            isLoading || (!hasProgressData && !isFinished && !hasJobError);

        const showCompletedBar = isFinished && !hasJobError && !hasProgressData;

        return (
            <div className={`${CSS_CLASS_NAME}__job-progress`} key={label}>
                <Paragraph>
                    <strong>{label}</strong>
                    {progress != null && (
                        <>
                            {" "}
                            — {progress.progressCount ?? 0} /{" "}
                            {progress.progressTotal ?? "?"} processed
                            {(progress.errorCount ?? 0) > 0 && (
                                <> ({progress.errorCount} error(s))</>
                            )}
                        </>
                    )}
                    {isFinished && !hasJobError && <> — Done</>}
                    {hasJobError && <> — Failed</>}
                    {showPlaceholder && <Loader size={LoaderSizes.Small} />}
                </Paragraph>
                {showCompletedBar && (
                    <ProgressBar
                        style={ProgressBarStyles.Thick}
                        cssClassName="-info"
                        value={100}
                    />
                )}
                {hasProgressData && (
                    <ProgressBar
                        style={ProgressBarStyles.Thick}
                        cssClassName={hasJobError ? "-error" : "-info"}
                        value={percent}
                    />
                )}
            </div>
        );
    };

    return (
        <div className={`${CSS_CLASS_NAME}__migration-progress`}>
            <div className={`${CSS_CLASS_NAME}__content`}>
                {!mainJob && !mainJobError && (
                    <>
                        <Paragraph>Starting migration…</Paragraph>
                        <Loader />
                    </>
                )}

                {mainJobError && (
                    <AlertBanner alertLevel={AlertLevels.Error}>
                        Failed to load migration job status.
                    </AlertBanner>
                )}

                {mainJob != null && !mainJobError && (
                    <>
                        {hasError ? (
                            <AlertBanner alertLevel={AlertLevels.Error}>
                                The migration encountered an error. Check the
                                details below.
                                {mainDebugJson?.Errors != null && (
                                    <Paragraph>
                                        {mainDebugJson.Errors}
                                    </Paragraph>
                                )}
                                {mainJob.error != null && (
                                    <Paragraph>
                                        Error details: {mainJob.error}
                                    </Paragraph>
                                )}
                            </AlertBanner>
                        ) : allFinished ? (
                            <AlertBanner alertLevel={AlertLevels.Success}>
                                Migration completed successfully.
                            </AlertBanner>
                        ) : (
                            <AlertBanner alertLevel={AlertLevels.Info}>
                                Migration is in progress. This may take a few
                                minutes.
                            </AlertBanner>
                        )}

                        {isWaitingForNestedJobs && !allFinished && (
                            <>
                                <Paragraph>
                                    Preparing background jobs…
                                </Paragraph>
                                <Loader />
                            </>
                        )}

                        {addMembersJobId != null &&
                            renderJobProgress(
                                "Adding members",
                                addMembersProgress,
                                addMembersFinished,
                                addMembersJobError ||
                                    addMembersJob?.status ===
                                        JobStatuses.Errored,
                                addMembersJob == null && !addMembersJobError
                            )}

                        {syncJobId != null &&
                            renderJobProgress(
                                "Syncing members",
                                syncProgress,
                                syncFinished,
                                syncJobError ||
                                    syncJob?.status === JobStatuses.Errored,
                                syncJob == null && !syncJobError
                            )}

                        {dataMigrationJobId != null &&
                            renderJobProgress(
                                "Migrating data",
                                dataMigrationProgress,
                                dataMigrationFinished,
                                dataMigrationJobError ||
                                    dataMigrationJob?.status ===
                                        JobStatuses.Errored,
                                dataMigrationJob == null &&
                                    !dataMigrationJobError
                            )}
                    </>
                )}
            </div>

            <BaseModalFooter
                primaryText={"Close"}
                primaryButtonProps={{
                    onClick: onClose,
                    disabled: !allFinished && !hasError,
                }}
                closeDialog={onClose}
            />
        </div>
    );
};

export default ModalPhaseMigrationProgress;

function parseDebugJson<T>(debugJson: string | undefined): T | null {
    if (!StringUtils.hasValue(debugJson)) return null;
    try {
        return JSON.parse(debugJson as string) as T;
    } catch {
        return null;
    }
}
